import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Download, FolderOpen, Trash2, Upload } from 'lucide-react'

import { Button, ExplainDisabled } from '@/components/ui/button'
import { EmptyState, ErrorNote, Spinner } from '@/components/ui/misc'
import { api, ApiError, type Bucket, type FolderRow } from '@/lib/api'
import { cn } from '@/lib/cn'
import { useAuth } from '@/features/auth/AuthProvider'
import { UploadDialog } from '@/features/upload/UploadDialog'
import { useUploads } from '@/features/upload/UploadProvider'
import { Breadcrumbs } from './Breadcrumbs'
import { BucketSidebar } from './BucketSidebar'
import { DeleteDialog } from './DeleteDialog'
import { DeleteFolderDialog } from './DeleteFolderDialog'
import { DetailDrawer } from './DetailDrawer'
import { ObjectTable } from './ObjectTable'
import { useListing } from './useListing'

export function BrowserPage() {
  const { canEdit, can } = useAuth()
  // Queueing now happens inside UploadDialog; this page only needs to know when
  // an upload finished so the listing can refresh.
  const { completionTick } = useUploads()
  const [searchParams, setSearchParams] = useSearchParams()

  const [buckets, setBuckets] = useState<Bucket[]>([])
  const [bucketsLoading, setBucketsLoading] = useState(true)
  const [bucketsError, setBucketsError] = useState<string | null>(null)

  const bucket = searchParams.get('bucket')
  const prefix = searchParams.get('prefix') ?? ''

  const listing = useListing(bucket, prefix)

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [drawerKey, setDrawerKey] = useState<string | null>(null)
  const [deleteKeys, setDeleteKeys] = useState<string[] | null>(null)
  const [deleteFolder, setDeleteFolder] = useState<FolderRow | null>(null)
  const [dragging, setDragging] = useState(false)
  // Upload is a two-step flow: stage files and choose retention in a dialog,
  // then queue. Retention is irreversible, so it should not be a setting left
  // sitting on a toolbar where it can be forgotten between uploads.
  const [uploadOpen, setUploadOpen] = useState(false)
  const [stagedFiles, setStagedFiles] = useState<File[]>([])

  const lastToggled = useRef<string | null>(null)
  const dragDepth = useRef(0)

  // --- buckets ------------------------------------------------------------

  useEffect(() => {
    api
      .buckets()
      .then((res) => {
        setBuckets(res.buckets)
        // Land on the first allow-listed bucket rather than an empty screen.
        if (!bucket && res.buckets[0]) {
          setSearchParams({ bucket: res.buckets[0].name }, { replace: true })
        }
      })
      .catch((err) =>
        setBucketsError(err instanceof ApiError ? err.message : 'Could not load buckets'),
      )
      .finally(() => setBucketsLoading(false))
    // Runs once; bucket selection afterwards is driven by the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Refresh the listing whenever an upload finishes so the new object appears.
  useEffect(() => {
    if (completionTick > 0) listing.refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completionTick])

  // Selection is per-folder; carrying it across navigation would let a user
  // delete something they can no longer see.
  useEffect(() => {
    setSelected(new Set())
    lastToggled.current = null
  }, [bucket, prefix])

  // --- navigation ---------------------------------------------------------

  const navigate = useCallback(
    (nextBucket: string, nextPrefix: string) => {
      const params: Record<string, string> = { bucket: nextBucket }
      if (nextPrefix) params.prefix = nextPrefix
      setSearchParams(params)
    },
    [setSearchParams],
  )

  // --- selection ----------------------------------------------------------

  const toggle = useCallback(
    (key: string, shiftKey: boolean) => {
      setSelected((current) => {
        const next = new Set(current)

        // Shift-click extends from the previous toggle, matching the range
        // selection people expect from a file manager.
        if (shiftKey && lastToggled.current) {
          const keys = listing.objects.map((o) => o.key)
          const from = keys.indexOf(lastToggled.current)
          const to = keys.indexOf(key)
          if (from >= 0 && to >= 0) {
            const [start, end] = from < to ? [from, to] : [to, from]
            const shouldSelect = !current.has(key)
            for (let i = start; i <= end; i += 1) {
              const k = keys[i] as string
              if (shouldSelect) next.add(k)
              else next.delete(k)
            }
            lastToggled.current = key
            return next
          }
        }

        if (next.has(key)) next.delete(key)
        else next.add(key)
        lastToggled.current = key
        return next
      })
    },
    [listing.objects],
  )

  const toggleAll = useCallback(() => {
    setSelected((current) => {
      const allKeys = listing.objects.map((o) => o.key)
      const allSelected = allKeys.length > 0 && allKeys.every((k) => current.has(k))
      return allSelected ? new Set() : new Set(allKeys)
    })
  }, [listing.objects])

  // --- uploads ------------------------------------------------------------

  const openUpload = useCallback((files: File[] = []) => {
    setStagedFiles(files)
    setUploadOpen(true)
  }, [])

  // Dropping onto the page stages the files in the dialog rather than starting
  // immediately, so retention and the file list can still be reviewed.
  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      dragDepth.current = 0
      setDragging(false)
      if (!canEdit) return
      const files = Array.from(event.dataTransfer.files)
      if (files.length > 0) openUpload(files)
    },
    [canEdit, openUpload],
  )

  // --- selection actions --------------------------------------------------

  const selectedKeys = useMemo(() => [...selected], [selected])
  const lockedSelected = selectedKeys.filter((key) => listing.metas.get(key)?.locked)

  const downloadSelected = useCallback(() => {
    if (!bucket) return
    // Each download is a separate top-level request; browsers throttle
    // simultaneous navigations, so they are staggered slightly.
    selectedKeys.forEach((key, index) => {
      window.setTimeout(() => {
        window.open(api.downloadUrl(bucket, key), '_blank')
      }, index * 300)
    })
  }, [bucket, selectedKeys])

  // Passed straight through: useListing guarantees a stable identity, and
  // wrapping it in a useCallback keyed on `listing` would reintroduce the
  // changing-identity problem this is meant to avoid.
  const onMetaLoaded = listing.setMeta

  // --- render -------------------------------------------------------------

  if (bucketsError) {
    return (
      <div className="p-6">
        <ErrorNote>{bucketsError}</ErrorNote>
      </div>
    )
  }

  const isEmpty =
    !listing.loading && listing.folders.length === 0 && listing.objects.length === 0

  return (
    <div className="flex h-full min-h-0">
      <BucketSidebar
        buckets={buckets}
        loading={bucketsLoading}
        selected={bucket}
        onSelect={(name) => navigate(name, '')}
      />

      <main
        className="relative flex min-w-0 flex-1 flex-col"
        onDragEnter={(e) => {
          e.preventDefault()
          if (!canEdit) return
          dragDepth.current += 1
          setDragging(true)
        }}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={(e) => {
          e.preventDefault()
          dragDepth.current -= 1
          if (dragDepth.current <= 0) setDragging(false)
        }}
        onDrop={onDrop}
      >
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3">
          {bucket ? (
            <Breadcrumbs
              bucket={bucket}
              prefix={prefix}
              onNavigate={(next) => navigate(bucket, next)}
            />
          ) : (
            <span className="text-sm text-muted-foreground">Select a bucket</span>
          )}

          <ExplainDisabled reason={canEdit ? null : 'Uploading requires the Editor role.'}>
            <Button variant="primary" disabled={!canEdit || !bucket} onClick={() => openUpload()}>
              <Upload className="h-4 w-4" />
              Upload
            </Button>
          </ExplainDisabled>
        </div>

        {/* Selection action bar */}
        {selectedKeys.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 border-b border-border bg-accent/8 px-5 py-2.5">
            <span className="text-sm font-medium">{selectedKeys.length} selected</span>
            <div className="flex gap-2">
              <Button size="sm" onClick={downloadSelected}>
                <Download className="h-4 w-4" />
                Download
              </Button>
              <ExplainDisabled
                reason={
                  !canEdit
                    ? 'Deleting requires the Editor role.'
                    : lockedSelected.length === selectedKeys.length
                      ? 'Every selected object is protected by retention or a hold.'
                      : null
                }
              >
                <Button
                  size="sm"
                  variant="danger"
                  disabled={!canEdit || lockedSelected.length === selectedKeys.length}
                  onClick={() => setDeleteKeys(selectedKeys)}
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </Button>
              </ExplainDisabled>
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
                Clear
              </Button>
            </div>
            {lockedSelected.length > 0 && lockedSelected.length < selectedKeys.length && (
              <span className="text-xs text-muted-foreground">
                {lockedSelected.length} protected by retention and will be skipped
              </span>
            )}
          </div>
        )}

        {/* Listing */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {listing.error && (
            <div className="p-5">
              <ErrorNote>{listing.error}</ErrorNote>
            </div>
          )}

          {isEmpty && !listing.error && (
            <EmptyState
              icon={<FolderOpen className="h-8 w-8" />}
              title="This folder is empty"
              description={
                canEdit
                  ? 'Drag files here, or use the Upload button.'
                  : 'Nothing has been uploaded here yet.'
              }
            />
          )}

          {!isEmpty && (
            <>
              <ObjectTable
                folders={listing.folders}
                objects={listing.objects}
                metas={listing.metas}
                selected={selected}
                onToggle={toggle}
                onToggleAll={toggleAll}
                onOpenFolder={(next) => bucket && navigate(bucket, next)}
                onOpenObject={setDrawerKey}
                onDeleteFolder={can('delete-directory') ? setDeleteFolder : undefined}
                loading={listing.loading}
              />

              {listing.hasMore && (
                <div className="flex justify-center py-4">
                  <Button onClick={listing.loadMore} loading={listing.loadingMore}>
                    Load more
                  </Button>
                </div>
              )}
            </>
          )}

          {listing.loading && listing.objects.length === 0 && !isEmpty && (
            <div className="flex justify-center py-10">
              <Spinner className="h-5 w-5" />
            </div>
          )}
        </div>

        {/* Drag overlay */}
        {dragging && canEdit && (
          <div
            className={cn(
              'pointer-events-none absolute inset-0 z-20 flex items-center justify-center',
              'border-2 border-dashed border-accent bg-accent/10',
            )}
          >
            <p className="rounded-md bg-background px-4 py-2 text-sm font-medium shadow">
              Drop files to add them to an upload
            </p>
          </div>
        )}
      </main>

      {bucket && uploadOpen && (
        <UploadDialog
          bucket={bucket}
          prefix={prefix}
          initialFiles={stagedFiles}
          onClose={() => {
            setUploadOpen(false)
            setStagedFiles([])
          }}
        />
      )}

      {bucket && (
        <DetailDrawer
          bucket={bucket}
          objectKey={drawerKey}
          onClose={() => setDrawerKey(null)}
          onDelete={(key) => {
            setDrawerKey(null)
            setDeleteKeys([key])
          }}
          onMetaLoaded={onMetaLoaded}
          onRenamed={(newKey) => {
            // Follow the file to its new name rather than leaving the drawer
            // pointing at a key that no longer exists.
            setDrawerKey(newKey)
            listing.refresh()
          }}
        />
      )}

      {bucket && deleteFolder && (
        <DeleteFolderDialog
          bucket={bucket}
          prefix={deleteFolder.prefix}
          name={deleteFolder.name}
          onClose={() => setDeleteFolder(null)}
          onDeleted={() => listing.refresh()}
        />
      )}

      {bucket && deleteKeys && (
        <DeleteDialog
          bucket={bucket}
          keys={deleteKeys}
          metas={listing.metas}
          onClose={() => setDeleteKeys(null)}
          onDeleted={(deleted) => {
            listing.removeKeys(deleted)
            setSelected((current) => {
              const next = new Set(current)
              for (const key of deleted) next.delete(key)
              return next
            })
          }}
        />
      )}
    </div>
  )
}
